/**
 * Each person's own settings (1.5.16 §6): language, appearance, text size, the last program tab and the last
 * Admin page — kept on the server per signed-in person, so a phone and a computer agree and a new device opens
 * the way the last one was left. GET /api/me carries them; PUT /api/me/settings takes a partial patch.
 *
 * Nothing here is an address: the Admin page is one of the console's own tab names and the program one of the
 * strip's three, so a stored value can never send anyone outside `/admin/<tab>`. No dependencies: the app and
 * the API share it.
 */
import type { MyNotice } from './new-187';

export const MY_LANGS = ['en', 'es'] as const;
export const MY_THEMES = ['light', 'dark', 'auto'] as const;
export const MY_SIZES = ['md', 'lg'] as const;
/** The program strip's three (src/app/program.tsx). */
export const MY_PROGRAMS = ['housekeeping', 'companion', 'reading'] as const;
/**
 * The Admin console's pages, in the order of its tabs (src/pages/admin/AdminLayout.tsx). 1.6.9: "slots" is gone —
 * Service days (/admin/service-days) is opened from Settings → Closed days and service days, not from a tab.
 */
export const ADMIN_PAGES = ['users', 'settings', 'templates', 'fixit', 'review', 'audit', 'health', 'import', 'export', 'reports', 'about'] as const;
export type AdminPage = (typeof ADMIN_PAGES)[number];
/**
 * 25 Oct 2026 (D-1025-ADMIN): Admin's first page shows its pages in four groups (proposal §3.9). Every entry of
 * ADMIN_PAGES is in exactly one group (a test holds it); `service-days` is a page of its own reached from Settings
 * and listed beside it, so it is not an ADMIN_PAGES entry.
 */
export const ADMIN_GROUPS = [
  { key: 'access', pages: ['users'] },
  { key: 'rules', pages: ['settings', 'templates', 'service-days'] },
  { key: 'records', pages: ['review', 'audit', 'import', 'export', 'reports'] },
  { key: 'system', pages: ['health', 'fixit', 'about'] },
] as const satisfies readonly { key: string; pages: readonly (AdminPage | 'service-days')[] }[];
export type AdminGroupKey = (typeof ADMIN_GROUPS)[number]['key'];

/** Where Admin opens for someone who has not been there yet. */
export const DEFAULT_ADMIN_PAGE: AdminPage = 'health';

export interface MySettingsValues {
  lang: (typeof MY_LANGS)[number] | null;
  theme: (typeof MY_THEMES)[number] | null;
  size: (typeof MY_SIZES)[number] | null;
  program: (typeof MY_PROGRAMS)[number] | null;
  adminPage: AdminPage | null;
}

/** What the server keeps for a person; a null is "not chosen yet" (the device's own default applies). */
export interface MySettings extends MySettingsValues {
  /** When the server last took a change (its own clock): a device that has seen this one has nothing to take. */
  updatedAt: string;
  /**
   * 1.8.4 (D-1.8.4-3): the name Today's greeting uses ("Good morning, Eli."); absent or null — their first name.
   * Free text, so it is kept apart from the choices below (MY_SETTING_KEYS): the device's display sync never sends it.
   */
  callMe?: string | null;
  /**
   * 1.8.5 (D-1.8.5-3): the tech's own "Email me about new tickets". Absent (never chosen) is on; only false is kept.
   * A switch, kept apart from the display choices like `callMe` (the device's display sync never sends it).
   */
  ticketEmails?: boolean;
  techMode?: 'guided' | 'expert';
  techSpeechRate?: number;
  /**
   * 1.8.7 (D-1.8.7-2): the notes this person pressed Got it on (shared/new-187.ts MY_NOTICES) - absent when none.
   * Kept apart from the display choices like `callMe`; a note is only ever added.
   */
  gotIt?: MyNotice[];
}

/** PUT /api/me/settings: only what changed; null puts a choice back to "not chosen". `callMe`: '' or null clears it. */
export type MySettingsPatch = Partial<MySettingsValues> & { callMe?: string | null; ticketEmails?: boolean; techMode?: 'guided' | 'expert'; techSpeechRate?: number; /** 1.8.7: one note to add to `gotIt`. */ gotIt?: string };

/** "Email me about new tickets" (1.8.5): on unless the person turned it off. */
export const ticketEmailsOn = (s: Pick<MySettings, 'ticketEmails'> | null | undefined): boolean => s?.ticketEmails !== false;

/** "Call me" (1.8.4): at most this many characters. */
export const CALL_ME_MAX = 40;

/**
 * A "Call me" as received: trimmed; '' and null are "not chosen" (null); undefined when it is not one — longer than
 * 40 characters, or carrying a control character (a line break, a tab, an escape).
 */
export function cleanCallMe(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/.test(s)) return undefined;
  if ([...s].length > CALL_ME_MAX) return undefined;
  return s;
}

export const MY_SETTING_KEYS = ['lang', 'theme', 'size', 'program', 'adminPage'] as const;
export type MySettingKey = (typeof MY_SETTING_KEYS)[number];

const ALLOWED: Record<MySettingKey, readonly string[]> = { lang: MY_LANGS, theme: MY_THEMES, size: MY_SIZES, program: MY_PROGRAMS, adminPage: ADMIN_PAGES };

/** A stored or received value, or null when it is not one of the known choices. */
export function knownSetting<K extends MySettingKey>(key: K, value: unknown): MySettingsValues[K] {
  return (typeof value === 'string' && ALLOWED[key].includes(value) ? value : null) as MySettingsValues[K];
}

export function isAdminPage(v: unknown): v is AdminPage {
  return knownSetting('adminPage', v) !== null;
}

/** The address Admin opens at for a person: always under /admin/, whatever was stored. */
export function adminHome(page: unknown): string {
  return `/admin/${isAdminPage(page) ? page : DEFAULT_ADMIN_PAGE}`;
}
